/**
 * VENDOR MODULES
 */
import React from 'react';
import { useTranslation } from "react-i18next";

import MatchResult from 'features/markets/components/football/singles/MatchResult';
import DoubleChance from 'features/markets/components/football/singles/DoubleChance';
import OverUnder from 'features/markets/components/football/singles/OverUnder';
import ExactScore from 'features/markets/components/football/singles/ExactScore';
import { cn } from 'lib/utils';
import { getSportOnColor, getSportLightColor, getSportTextColorClasses } from 'shared/utils/sport-colors';
import GridOdd from 'shared/components/outcomes/grids/GridOdd';
import { vsMarketTypeKey, vsOutcomeTypeKey } from 'lib/virtualSportsI18n';
import { getTeamCode } from 'shared/utils/teamDisplay';

const FOOTBALL3D_YELLOW = '#ffd03a'; // platform-tv
const FOOTBALL3D_BLUE = '#1a3a5f'; // platform-tv

/**
 * Renders the bet area for Football 3D
 *
 * @class      Football3dArea (name)
 * @param      {<type>}   props   The properties
 * @return     {boolean}  { description_of_the_return_value }
 */
function Football3dArea(props) {
  const { t } = useTranslation();
  const {
    event = {},
    showHeader = true,
  } = props;

  const [selectedGroup, setSelectedGroup] = React.useState('main'); // main | ou | exact | combo

  const { outcomes, home_team, away_team } = event;
  const sport_id = event?.sport_id || 50; // Default to 50 (Football 3D)
  
  // Global sport theme variables; set on root so --sport-light-color is consistent when nested.
  const sportColor = 'var(--sport-color)';
  const sportLightColor = getSportLightColor(sport_id);
  const textColorSport = getSportTextColorClasses(sport_id);
  const sportOnColor = getSportOnColor(sport_id);

  // Football: use TEAM CODE only (desktop + mobile)
  const homeTeamCode = getTeamCode(home_team, event?.home_team_code) || t('HOME');
  const awayTeamCode = getTeamCode(away_team, event?.away_team_code) || t('AWAY');

  const MarketLabel = ({ marketTypeId }) => (
    <>
      <span>{t(vsMarketTypeKey(marketTypeId, false))}</span>
    </>
  );

  const OutcomeLabel = ({ outcomeTypeId }) => (
    <>
      <span className="md:hidden">{t(vsOutcomeTypeKey(outcomeTypeId, true))}</span>
      <span className="hidden md:inline">{t(vsOutcomeTypeKey(outcomeTypeId, false))}</span>
    </>
  );

  const outcomesByMarketType = React.useMemo(() => {
    const map = new Map();
    for (const o of Object.values(outcomes || {})) {
      const mt = parseInt(o.market_type_id);
      if (!Number.isFinite(mt)) continue;
      if (!map.has(mt)) map.set(mt, []);
      map.get(mt).push(o);
    }
    return map;
  }, [outcomes]);

  const filterOutcomes = React.useCallback((marketTypeId) => {
    return outcomesByMarketType.get(marketTypeId) || [];
  }, [outcomesByMarketType]);

  const comboMarketTypeIds = React.useMemo(() => {
    const known = new Set([5001, 5002, 5003, 5004, 5005, 5006]);
    return Array.from(outcomesByMarketType.keys())
      .filter((mt) => !known.has(mt))
      .sort((a, b) => a - b);
  }, [outcomesByMarketType]);

  const overUnderThresholds = React.useMemo(() => {
    const ouOutcomes = outcomesByMarketType.get(5003) || [];
    const thresholds = Array.from(
      new Set(
        ouOutcomes
          .map((o) => String(o?.outcome || ''))
          .filter((s) => s.startsWith('UNDER_') || s.startsWith('OVER_'))
          .map((s) => s.split('_')[1])
          .filter(Boolean)
      )
    ).sort((a, b) => Number(a) - Number(b));
    return thresholds;
  }, [outcomesByMarketType]);

  const getOverUnderLabel = React.useCallback((outcomeObj, threshold) => {
    const outcomeTypeId = outcomeObj?.outcome_type_id;
    if (outcomeTypeId != null) {
      return t(vsOutcomeTypeKey(outcomeTypeId, false));
    }
    const raw = String(outcomeObj?.outcome || '');
    if (raw.startsWith('UNDER_')) return `${t('UNDER')} ${threshold}`;
    if (raw.startsWith('OVER_')) return `${t('OVER')} ${threshold}`;
    return '';
  }, [t]);

  const MarketSectionHeader = ({ marketTypeId }) => (
    <div
      className="text-xs md:text-sm border-b p-2 text-center"
      style={{ backgroundColor: FOOTBALL3D_BLUE, borderColor: FOOTBALL3D_BLUE }}
    >
      <span className="font-semibold" style={{ color: FOOTBALL3D_YELLOW }}>
        <MarketLabel marketTypeId={marketTypeId} />
      </span>
    </div>
  );

  const tabBtnClass = (isSelected) => cn(
    "w-full px-3 py-2 md:py-2.5 text-center cursor-pointer transition-colors text-xs rounded-md border-[3px] font-semibold",
    isSelected
      ? "border-[var(--sport-color)] bg-card text-foreground"
      : "border-border bg-card text-foreground hover:border-[var(--sport-color)]"
  );

  return (
    <div className="football3d-area w-full min-w-0" data-sport-id={sport_id} style={{ '--sport-color': sportColor, '--sport-light-color': sportLightColor, '--sport-on-color': sportOnColor }}>
      {/* Match Header */}
      {showHeader && (
        <div 
          className="match-header p-3 text-center border-b"
          style={{ backgroundColor: FOOTBALL3D_YELLOW, borderColor: FOOTBALL3D_YELLOW }}
        >
          <span className="font-bold text-lg" style={{ color: FOOTBALL3D_BLUE }}>
            {homeTeamCode} <span className="mx-2 opacity-75">vs</span> {awayTeamCode}
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-1 border-0 p-2 w-full min-w-0">
        <button
            type="button"
            onClick={() => setSelectedGroup('main')}
            className={tabBtnClass(selectedGroup === 'main')}
          >
            <span className="block px-1 whitespace-normal break-words leading-tight">
              {t('FOOTBALL3D_GROUP_MAIN_MARKETS')}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setSelectedGroup('ou')}
            className={tabBtnClass(selectedGroup === 'ou')}
          >
            <span className="block px-1 whitespace-normal break-words leading-tight">
              {t('FOOTBALL3D_GROUP_OVER_UNDER')}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setSelectedGroup('exact')}
            className={tabBtnClass(selectedGroup === 'exact')}
          >
            <span className="block px-1 whitespace-normal break-words leading-tight">
              <MarketLabel marketTypeId={5004} />
            </span>
          </button>
          <button
            type="button"
            onClick={() => setSelectedGroup('combo')}
            className={tabBtnClass(selectedGroup === 'combo')}
          >
            <span className="block px-1 whitespace-normal break-words leading-tight">
              {t('FOOTBALL3D_GROUP_COMBO_MARKETS')}
            </span>
          </button>
        </div>

        {/* MAIN MARKETS */}
        {selectedGroup === 'main' && (
          <div className="w-full space-y-3">
            {/* 1X2 */}
            <div className="w-full">
              <MarketSectionHeader marketTypeId={5001} />
              <MatchResult outcomes={outcomes} homeTeamName={homeTeamCode} awayTeamName={awayTeamCode} event={event} oddVariant="grid-odd-football3d" />
            </div>

            {/* Double Chance */}
            <div className="w-full">
              <MarketSectionHeader marketTypeId={5002} />
              <DoubleChance outcomes={outcomes} homeTeamName={homeTeamCode} awayTeamName={awayTeamCode} event={event} oddVariant="grid-odd-football3d" />
            </div>

            {/* Goal/No Goal */}
            <div className="w-full">
              <MarketSectionHeader marketTypeId={5005} />
              <div className="grid grid-cols-2 gap-2 p-4">
                {filterOutcomes(5005).map(outcome => (
                  <GridOdd
                    key={outcome.outcome_id}
                    {...{
                      ...outcome,
                      class_names: ['grid-odd-football3d'],
                      market_type_group_id: 512,
                      display_label: outcome.outcome_type_id
                        ? t(vsOutcomeTypeKey(outcome.outcome_type_id, false))
                        : '',
                      event
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* OVER / UNDER */}
        {selectedGroup === 'ou' && (
          <div className="w-full space-y-3">
            {/* Over/Under */}
            <div className="w-full">
              <MarketSectionHeader marketTypeId={5003} />
              <div className="divide-y divide-border">
                {overUnderThresholds.map((threshold) => {
                  const list = filterOutcomes(5003);
                  const underOutcome = list.find((o) => String(o?.outcome) === `UNDER_${threshold}`);
                  const overOutcome = list.find((o) => String(o?.outcome) === `OVER_${threshold}`);
                  return (
                    <div key={String(threshold)} className="grid grid-cols-2 gap-3 p-4">
                      <div className="w-full">
                        {underOutcome && (
                          <GridOdd
                            key={underOutcome.outcome_id}
                            {...{
                              ...underOutcome,
                              class_names: ['grid-odd-football3d', 'w-full', 'p-4', 'min-h-[72px]'],
                              market_type_group_id: 510,
                              display_label: getOverUnderLabel(underOutcome, threshold),
                              event,
                            }}
                          />
                        )}
                      </div>
                      <div className="w-full">
                        {overOutcome && (
                          <GridOdd
                            key={overOutcome.outcome_id}
                            {...{
                              ...overOutcome,
                              class_names: ['grid-odd-football3d', 'w-full', 'p-4', 'min-h-[72px]'],
                              market_type_group_id: 510,
                              display_label: getOverUnderLabel(overOutcome, threshold),
                              event,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Total Goals */}
            <div className="w-full">
              <MarketSectionHeader marketTypeId={5006} />
              <div className="grid grid-cols-3 gap-2 p-4">
                {filterOutcomes(5006)
                  .slice()
                  .sort((a, b) => Number(a.outcome_type_id) - Number(b.outcome_type_id))
                  .map((outcome) => (
                    <GridOdd
                      key={outcome.outcome_id}
                      {...{
                        ...outcome,
                        class_names: ['grid-odd-football3d'],
                        market_type_group_id: 513,
                        display_label: outcome.outcome_type_id
                          ? (window.matchMedia && window.matchMedia('(min-width: 768px)').matches
                            ? t(vsOutcomeTypeKey(outcome.outcome_type_id, false))
                            : t(vsOutcomeTypeKey(outcome.outcome_type_id, true)))
                          : '',
                        event
                      }}
                    />
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* EXACT SCORE */}
        {selectedGroup === 'exact' && (
          <div className="w-full">
              <ExactScore
              outcomes={outcomes}
              homeTeamName={homeTeamCode}
              awayTeamName={awayTeamCode}
              maxGoals={4}
              event={event}
              orderByResult
              oddVariant="grid-odd-football3d"
            />
          </div>
        )}

        {/* COMBO MARKETS */}
        {selectedGroup === 'combo' && (
          <>
            {comboMarketTypeIds.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">{t('NO_MARKETS_AVAILABLE') || 'No markets available'}</div>
            ) : (
              <div className="w-full space-y-3">
                {comboMarketTypeIds.map((mt) => (
                  <div key={String(mt)} className="w-full">
                    <MarketSectionHeader marketTypeId={mt} />
                    <div className="grid grid-cols-2 gap-2 p-4">
                      {filterOutcomes(mt)
                        .slice()
                        .sort((a, b) => Number(a.outcome_type_id) - Number(b.outcome_type_id))
                        .map((outcome) => (
                          <GridOdd
                            key={outcome.outcome_id}
                            {...{
                              ...outcome,
                              class_names: ['grid-odd-football3d'],
                              market_type_group_id: 514,
                              display_label: outcome.outcome_type_id
                                ? (window.matchMedia && window.matchMedia('(min-width: 768px)').matches
                                  ? t(vsOutcomeTypeKey(outcome.outcome_type_id, false))
                                  : t(vsOutcomeTypeKey(outcome.outcome_type_id, true)))
                                : '',
                              event
                            }}
                          />
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
    </div>
  );
}

export default Football3dArea;
export { FOOTBALL3D_YELLOW };

